import { normalizeQcm, trimQcmOptions } from './normalizers/normalizeQcm';
import { normalizeSaisie } from './normalizers/normalizeSaisie';
import { normalizeVraiFaux } from './normalizers/normalizeVraiFaux';
import { HtmlWithKatex } from './helpers/renderKatex';
import PrintConstructionFigure from './helpers/renderConstructionFigure';
import PrintNumberLine from './helpers/renderNumberLine';
import PrintRepere2D from './helpers/renderRepere2D';
import { seededShuffle, seedFromPayload } from './helpers/seededShuffle';
import { VraiFauxBody } from './renderers/PrintVraiFaux';
import { isVraiFaux, MAX_GROUPED_OPTIONS } from './helpers/groupQuestions';
import { qcmConvertible, estTableau, tableConvertible, reponseTable, estColonnesVraiFaux } from './helpers/reponseLibre';
import { besoinItemPx, longueurTexte, styleGrille } from './helpers/grilleReponses';
import { formatDateFr } from './consignes';
import { nomCouleur } from './helpers/couleurs';
import HorlogeAiguillesRenderer from '../VueQuestion/renderers/HorlogeAiguillesRenderer';

/**
 * Section de corrections, imprimée sur une page séparée
 * (`page-break-before: always`, flux natif → jamais de page blanche).
 *
 * Layout : grille 2 colonnes — les corrections courtes (QCM, saisies, trous,
 * ordonner) se rangent à deux par ligne ; les larges (V/F, relier, classer,
 * figures) prennent toute la largeur (`ep-corrections-large`).
 *
 * Réponses visuelles quand les données le permettent :
 *   - programme_construction → FIGURE FINALE (formes de solution colorées
 *     par tâche, via la prop `solutions` de PrintConstructionFigure) ;
 *   - placer_points → droite graduée avec les points placés ;
 *   - QCM / saisies groupés → chips « 1 → A » au lieu de texte collé.
 *
 * Fallbacks : `"Réponse ouverte — à évaluer selon le barème."` (saisie sans
 * bonne_reponse) ou `"À évaluer manuellement."` (type/parse inconnu).
 */

const VERT_SOLUTION = '#1D9E75';

/** Chips « n → réponse » (QCM groupés, saisies groupées, trous) — réponse en vert. */
const ChipsReponses = ({ entries }) => (
    <div className="ep-corrections-chips">
        {entries.map(({ cle, valeur }, i) => (
            <span key={i} className="ep-corrections-chip">
                <span className="ep-corrections-chip-cle">{cle}</span>
                <span className="ep-corrections-chip-fleche">→</span>
                <HtmlWithKatex as="span" className="ep-corrections-ok" html={String(valeur)} />
            </span>
        ))}
    </div>
);

/**
 * Corrigé « relier » : liens TRACÉS entre les deux colonnes — la disposition
 * reproduit exactement la feuille élève (même mélange seedé des cibles).
 * Lignes de hauteur fixe → extrémités des traits calculables sans mesure DOM.
 */
const LIGNE_H = 34;
const COULEURS_LIENS = ['#0e7a4b', '#4f6df5', '#f0663f', '#8a5cf6', '#b45309', '#0e7490'];

const RelierCorrection = ({ pairs, ciblesLeurres, seed }) => {
    if (!pairs?.length) return <div className="ep-corrections-answer">À évaluer manuellement.</div>;
    const sources = pairs.map((p) => p.source);
    // MÊME construction de liste ET même mélange que PrintAssociation (vraies
    // cibles puis leurres, même seed) → les positions correspondent à la feuille
    const targets = seededShuffle(
        [...pairs.map((p) => p.target), ...(ciblesLeurres || []).map((l) => l.target)],
        seed,
    );
    const cibleVers = pairs.map((p) => targets.indexOf(p.target));
    // Hauteur = la plus haute des deux colonnes (avec leurres, la droite dépasse)
    const H = Math.max(pairs.length, targets.length) * LIGNE_H;
    const W = 70;
    return (
        <div className="ep-corrections-relier">
            <div className="ep-corrections-relier-col">
                {sources.map((s, i) => (
                    <div key={i} className="ep-corrections-relier-cell">
                        <HtmlWithKatex as="span" html={s} />
                    </div>
                ))}
            </div>
            <svg
                className="ep-corrections-relier-svg"
                viewBox={`0 0 ${W} ${H}`}
                width={W}
                height={H}
                aria-hidden="true"
            >
                {cibleVers.map((j, i) => (
                    <line
                        key={i}
                        x1={0}
                        y1={i * LIGNE_H + LIGNE_H / 2}
                        x2={W}
                        y2={j * LIGNE_H + LIGNE_H / 2}
                        stroke={COULEURS_LIENS[i % COULEURS_LIENS.length]}
                        strokeWidth={2}
                        strokeLinecap="round"
                    />
                ))}
            </svg>
            <div className="ep-corrections-relier-col">
                {targets.map((t, i) => (
                    <div key={i} className="ep-corrections-relier-cell">
                        <HtmlWithKatex as="span" html={t} />
                    </div>
                ))}
            </div>
        </div>
    );
};

/** Corrigé « classer » : le tableau de l'exercice, REMPLI (items en vert). */
const ClasserCorrection = ({ categories }) => {
    if (!categories?.length) return <div className="ep-corrections-answer">À évaluer manuellement.</div>;
    return (
        <table className="ep-classer-table ep-classer-table-corrige">
            <thead>
                <tr>
                    {categories.map((cat, i) => (
                        <th key={i}>{cat.name}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                <tr>
                    {categories.map((cat, i) => (
                        <td key={i} className="ep-corrections-ok">
                            {(cat.items || []).map((item, k) => (
                                <div key={k}><HtmlWithKatex as="span" html={item} /></div>
                            ))}
                        </td>
                    ))}
                </tr>
            </tbody>
        </table>
    );
};

/** Parse le JSON bonne_reponse (string ou objet) — null si invalide. */
const parseBonneReponse = (brut) => {
    try {
        const parsed = typeof brut === 'string' ? JSON.parse(brut) : brut;
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

/** Corrigé d'un programme de construction : légende des étapes + figure finale. */
const ConstructionCorrection = ({ contenu }) => {
    const reponses = parseBonneReponse(contenu?.bonne_reponse);
    const tasks = Array.isArray(contenu?.tasks) ? contenu.tasks : [];
    if (!reponses || !tasks.length) {
        return (
            <div className="ep-corrections-answer">
                Construction : {tasks.length} étape{tasks.length > 1 ? 's' : ''} — à évaluer visuellement.
            </div>
        );
    }
    const solutions = tasks
        .flatMap((task) => [
            {
                color: task.color || VERT_SOLUTION,
                shape: reponses[task.id],
                // Propagé pour que le corrigé papier montre la même chose que l'écran :
                // deux points seuls, sans le trait qui les joint.
                pointsOnly: task.pointsOnly,
            },
            // `extraShapes` : côtés auto-tracés à la validation côté élève, typiquement
            // ceux qui referment le triangle. Ils ne sont pas dans `reponses`, si bien
            // que le corrigé papier montrait les arcs et le sommet, mais pas le triangle.
            ...(task.extraShapes || []).map((s) => ({
                color: s.color || task.color || VERT_SOLUTION,
                shape: s,
            })),
        ])
        .filter((s) => s.shape);
    return (
        <>
            <ul className="ep-corrections-pc-legende">
                {tasks.map((task, i) => (
                    <li key={task.id ?? i}>
                        <span
                            className="ep-corrections-pc-chip"
                            style={{ background: task.color || VERT_SOLUTION }}
                        >
                            {task.id || String.fromCharCode(97 + i)}
                        </span>
                        {nomCouleur(task.color) ? (
                            <span className="ep-pc-chip-nom">({nomCouleur(task.color)})</span>
                        ) : null}
                        <HtmlWithKatex as="span" html={task.consigne || ''} />
                    </li>
                ))}
            </ul>
            <p className="ep-pc-unite">Sur la figure : 1 carreau = 1 {contenu.unite || 'cm'}.</p>
            <div className="ep-corrections-figure">
                <PrintConstructionFigure
                    viewBox={contenu.viewBox}
                    pxPerCm={contenu.pxPerCm}
                    points={contenu.points}
                    preShapes={contenu.preShapes || []}
                    solutions={solutions}
                />
            </div>
        </>
    );
};

/** Corrigé placer_points : droite graduée avec les points placés. */
const PlacerPointsCorrection = ({ contenu }) => {
    const reponses = parseBonneReponse(contenu?.bonne_reponse);
    const nl = contenu?.numberline;
    const entries = reponses ? Object.entries(reponses) : [];
    if (!nl || !entries.length) {
        if (!entries.length) return <div className="ep-corrections-answer">À évaluer manuellement.</div>;
        return (
            <ul className="ep-corrections-vf">
                {entries.map(([label, value], i) => (
                    <li key={i}><strong>{label}</strong> → {String(value)}</li>
                ))}
            </ul>
        );
    }
    // showValue: true — la correction ANNONCE la réponse sous le point vert.
    // (Avant, sa lisibilité reposait sur le bug « toutes les graduations
    // étiquetées » du print, corrigé dans renderNumberLine : sans ce flag,
    // la valeur solution aurait disparu de la feuille de corrections.)
    const points = entries.map(([label, value]) => ({
        value: Number(value),
        label,
        color: VERT_SOLUTION,
        showValue: true,
    })).filter((p) => Number.isFinite(p.value));
    return (
        <div className="ep-corrections-figure ep-corrections-figure-nl">
            {/* Figure à 70 % de la pleine largeur : viewBox 600 explicite */}
            <PrintNumberLine {...nl} points={points} largeur="full" />
        </div>
    );
};

// ─── Corrections « single » (par type) ─────────────────────────────────

/** Bonnes options d'un QCM (contenu, pas lettre) — corrigé d'un QCM converti
 *  en réponse libre : la lettre n'existe plus sur la feuille de l'élève. */
const bonnesOptionsQcm = (norm) => {
    const ok = new Set(norm.correctLetters);
    return norm.options.filter((o) => ok.has(o.letter));
};

/** Réponses d'un tableau converti en libre : « ligne → colonne ». */
const reponsesTableLibre = (payload) => {
    const { lignes, colonnes, correctIndices } = reponseTable(payload);
    return lignes.map((ligne, i) => ({ ligne, col: colonnes[correctIndices[i]] ?? '?' }));
};

/** Repère a. b. c. — le même que sur la feuille (liste en lower-alpha). */
const repereLettre = (i) => `${String.fromCharCode(97 + (i % 26))}.`;

/** Liste « a. ligne → colonne » d'un tableau libre, sur la même grille
 *  réutilisable que la feuille (autant de colonnes que la place le permet). */
const besoinCorrige = (items) => besoinItemPx({
    texteMax: items.reduce((m, it) => Math.max(m, longueurTexte(it.ligne) + 3 + longueurTexte(it.col)), 0),
    repW: 0,
});

const ListeReponsesTable = ({ items }) => (
    <ul className="ep-corrections-vf ep-grille-reponses" style={styleGrille(besoinCorrige(items))}>
        {items.map((it, i) => (
            <li key={i}>
                <strong className="ep-corrections-repere">{repereLettre(i)}</strong>{' '}
                <HtmlWithKatex html={it.ligne} /> → <strong>{it.col}</strong>
            </li>
        ))}
    </ul>
);

const answerForSingle = (payload, libre = false) => {
    // V/F ET tableaux à colonnes nommées (avant : seuls les V/F ; un tableau
    // colonnesLibres non groupé tombait dans la branche QCM et imprimait la
    // chaîne brute `[ligne|colonne]…`).
    if (estTableau(payload)) {
        const { lignes, colonnes, correctIndices } = normalizeVraiFaux(payload);
        if (!lignes.length) return { text: 'À évaluer manuellement.' };
        if (libre && tableConvertible(payload)) {
            return { custom: <ListeReponsesTable items={reponsesTableLibre(payload)} /> };
        }
        // Tableau de l'exercice, bonnes cases COCHÉES (VraiFauxBody partagé)
        return {
            large: true,
            custom: (
                <VraiFauxBody
                    lignes={lignes}
                    colonnes={colonnes}
                    estVF={isVraiFaux(payload)}
                    correctIndices={correctIndices}
                />
            ),
        };
    }
    if (payload?.type === 'qcm') {
        // QCM multi "trop gros" (correct > 4) — affichage complet, pas de trim
        const norm = normalizeQcm(payload);
        const { correctLetters, multi } = norm;
        if (!correctLetters.length) return { text: 'À évaluer manuellement.' };
        const label = multi || correctLetters.length > 1 ? 'Réponses' : 'Réponse';
        if (libre && qcmConvertible(payload)) {
            const bonnes = bonnesOptionsQcm(norm);
            return {
                custom: (
                    <div className="ep-corrections-answer">
                        {label} :{' '}
                        {bonnes.map((o, i) => (
                            <span key={o.letter}>
                                {i > 0 ? ' ; ' : ''}
                                <HtmlWithKatex as="span" className="ep-corrections-ok" html={o.content} />
                            </span>
                        ))}
                    </div>
                ),
            };
        }
        return {
            custom: (
                <div className="ep-corrections-answer">
                    {label} : <span className="ep-corrections-ok">{correctLetters.join(' et ')}</span>
                </div>
            ),
        };
    }
    if (payload?.type === 'texte') {
        const { correctAnswer, suffixe } = normalizeSaisie(payload);
        if (!correctAnswer) {
            return { text: 'Réponse ouverte — à évaluer selon le barème.' };
        }
        return {
            custom: (
                <div className="ep-corrections-answer">
                    Réponse : <span className="ep-corrections-ok">{correctAnswer}{suffixe ? ' ' + suffixe : ''}</span>
                </div>
            ),
        };
    }
    if (payload?.type === 'association') {
        const c = payload.contenu || {};
        if (c.subType === 'relier') {
            return { large: true, custom: <RelierCorrection pairs={c.pairs} ciblesLeurres={c.cibles_leurres} seed={seedFromPayload(payload)} /> };
        }
        if (c.subType === 'ordonner') {
            const items = c.items || [];
            if (!items.length) return { text: 'À évaluer manuellement.' };
            return {
                chips: items.map((it, i) => ({ cle: String(i + 1), valeur: it })),
            };
        }
        if (c.subType === 'classer') {
            return { large: true, custom: <ClasserCorrection categories={c.categories} /> };
        }
        if (c.subType === 'texte_trou') {
            const answers = [...(c.phrase || '').matchAll(/\{\{([^}]+)\}\}/g)].map((m) => m[1].trim());
            if (!answers.length) return { text: 'À évaluer manuellement.' };
            return {
                chips: answers.map((mot, i) => ({ cle: `(${i + 1})`, valeur: mot })),
            };
        }
        if (c.subType === 'placer_points') {
            return { large: true, custom: <PlacerPointsCorrection contenu={c} /> };
        }
        
        if (c.subType === 'placer_points_2d') {
            try {
                const parsed = typeof c.bonne_reponse === 'string'
                    ? JSON.parse(c.bonne_reponse)
                    : (c.bonne_reponse || {});
                const entries = Object.entries(parsed);
                if (!entries.length) return { text: 'À évaluer manuellement.' };
                // Quadrillage sans axes (on compte des carreaux, 2026-09-24) : le corrigé
                // dessine la figure attendue, points reliés, plutôt que des coordonnées.
                if (c.repere?.quadrillage === true) {
                    const points = entries.map(([label, value]) => {
                        const [x, y] = String(value).split(';').map((v) => parseFloat(String(v).replace(',', '.')));
                        return { x, y, label };
                    }).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
                    return {
                        large: true,
                        custom: <PrintRepere2D repere={c.repere} dejaPlaces={c.dejaPlaces || []} decor={c.decor || null} relier={c.relier || null} points={points} />,
                    };
                }
                // Valeur canonique "x;y" → affichage "(x ; y)"
                return { items: entries.map(([label, value]) => ({ ligne: label, col: `(${String(value).replace(';', ' ; ')})` })) };
            } catch {
                return { text: 'À évaluer manuellement.' };
            }
        }

        if (c.subType === 'programme_construction') {
            return { large: true, custom: <ConstructionCorrection contenu={c} /> };
        }

        if (c.subType === 'placer_aiguilles') {
            // Le corrige papier montre l'horloge AVEC les aiguilles bien
            // placees (demande prof 2026-08-22) + la lecture en clair.
            try {
                const cible = JSON.parse(c.bonne_reponse).aiguilles;
                const sec = cible.sec != null ? ` min ${String(cible.sec).padStart(2, '0')} s` : '';
                const petite = cible.min === 0
                    ? `pile sur le ${cible.h}`
                    : `entre le ${cible.h} et le ${cible.h % 12 + 1}`;
                return {
                    custom: (
                        <div className="ep-corrections-answer">
                            <HorlogeAiguillesRenderer reponse={c.bonne_reponse} light />
                            <div>{`${cible.h} h ${String(cible.min).padStart(2, '0')}${sec} (petite aiguille ${petite}).`}</div>
                        </div>
                    ),
                };
            } catch {
                return { text: 'À évaluer manuellement.' };
            }
        }
    }
    return { text: 'À évaluer manuellement.' };
};

// ─── Items ─────────────────────────────────────────────────────────────

const ItemCorrection = ({ exNum, title, large, children }) => (
    <li className={`ep-corrections-item${large ? ' ep-corrections-large' : ''}`}>
        <span className="ep-corrections-num">{exNum}</span>
        <div className="ep-corrections-body">
            {title && <div className="ep-corrections-title">{title}</div>}
            {children}
        </div>
    </li>
);

const QcmGroupCorrection = ({ exNum, items, libre = false }) => {
    const entries = items.map(({ payload }, idx) => {
        const norm = trimQcmOptions(normalizeQcm(payload), MAX_GROUPED_OPTIONS);
        const { correctLetters } = norm;
        if (!correctLetters.length) return { cle: String(idx + 1), valeur: '?' };
        // Sous-question convertie en réponse libre → contenu des bonnes
        // options (les lettres n'existent plus sur la feuille).
        const valeur = libre && qcmConvertible(payload)
            ? bonnesOptionsQcm(norm).map((o) => o.content).join(' ; ')
            : correctLetters.join(', ');
        return { cle: String(idx + 1), valeur };
    });
    return (
        <ItemCorrection exNum={exNum} title={libre ? 'Questions' : 'QCM'}>
            <ChipsReponses entries={entries} />
        </ItemCorrection>
    );
};

const SaisieGroupCorrection = ({ exNum, items }) => {
    const entries = items.map(({ payload }, idx) => {
        const ans = payload?.contenu?.bonne_reponse;
        const suffixe = payload?.contenu?.suffixe || '';
        return {
            cle: String(idx + 1),
            valeur: ans == null || ans === '' ? '?' : `${ans}${suffixe ? ' ' + suffixe : ''}`,
        };
    });
    return (
        <ItemCorrection exNum={exNum} title="Saisies">
            <ChipsReponses entries={entries} />
        </ItemCorrection>
    );
};

const VfGroupCorrection = ({ exNum, items, libre = false }) => {
    const unVraiVF = items.some(({ payload }) => estColonnesVraiFaux(normalizeVraiFaux(payload).colonnes));
    return (
        <ItemCorrection exNum={exNum} title={unVraiVF ? 'Vrai / Faux' : 'Tableaux'} large>
            <div className="ep-corrections-vf-tables">
                {items.map(({ payload, originalIndex }, subIdx) => {
                    const { lignes, colonnes, correctIndices } = normalizeVraiFaux(payload);
                    return (
                        <div key={payload?.id ?? `sub-${originalIndex ?? subIdx}`}>
                            {items.length > 1 && (
                                <div className="ep-corrections-title">{subIdx + 1}.</div>
                            )}
                            {libre && tableConvertible(payload) ? (
                                <ListeReponsesTable items={reponsesTableLibre(payload)} />
                            ) : (
                                <VraiFauxBody
                                    lignes={lignes}
                                    colonnes={colonnes}
                                    estVF={isVraiFaux(payload)}
                                    correctIndices={correctIndices}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </ItemCorrection>
    );
};

/** Corrigé d'un bloc mixte à figure partagée : items repérés a. b. c. (comme
 *  la feuille), chaque réponse via answerForSingle (types hétérogènes). */
const FigureGroupCorrection = ({ exNum, items, libre = false }) => (
    <ItemCorrection exNum={exNum} large>
        <div className="ep-corrections-vf-tables">
            {items.map(({ payload, originalIndex }, idx) => {
                const answer = answerForSingle(payload, libre);
                return (
                    <div key={payload?.id ?? `sub-${originalIndex ?? idx}`}>
                        <div className="ep-corrections-title">{repereLettre(idx)}</div>
                        {answer.custom}
                        {answer.text && <div className="ep-corrections-answer">{answer.text}</div>}
                        {answer.chips && <ChipsReponses entries={answer.chips} />}
                        {answer.items && (
                            <ul className="ep-corrections-vf">
                                {answer.items.map((it, i) => (
                                    <li key={i}>
                                        <HtmlWithKatex html={it.ligne} /> → <strong>{it.col}</strong>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    </ItemCorrection>
);

const SingleCorrection = ({ exNum, payload, libre = false }) => {
    const title = payload?._meta?.label || '';
    const answer = answerForSingle(payload, libre);
    const estLarge = Boolean(answer.large);
    return (
        <ItemCorrection exNum={exNum} title={title} large={estLarge}>
            {answer.custom}
            {answer.text && <div className="ep-corrections-answer">{answer.text}</div>}
            {answer.chips && <ChipsReponses entries={answer.chips} />}
            {answer.items && (
                <ul className={`ep-corrections-vf${answer.items.length >= 6 ? ' ep-corrections-vf-2col' : ''}`}>
                    {answer.items.map((it, i) => (
                        <li key={i}>
                            <HtmlWithKatex html={it.ligne} /> → <strong>{it.col}</strong>
                        </li>
                    ))}
                </ul>
            )}
        </ItemCorrection>
    );
};

/** En-tête « Corrigé » — rendu sur la première page de corrigé (et sondé
 *  en phase mesure pour le budget). */
export const CorrigeHeading = ({ meta = {} }) => {
    const subtitle = [meta.titre, meta.classe, formatDateFr(meta.date)].filter(Boolean).join(' · ');
    return (
        <div className="ep-corrections-head">
            <h2 className="ep-corrections-heading">Corrigé</h2>
            {subtitle && <span className="ep-corrections-sub">{subtitle}</span>}
        </div>
    );
};

/**
 * Nœud de correction pour UN bloc (dispatch par kind). COMPOSANT (pas une
 * fabrique d'élément) : `block.exNum` est lu AU RENDU, donc la phase display
 * (post-renumberBlocks) affiche toujours les numéros finaux même si la liste
 * d'items est mémoïsée.
 * `reponseLibre` : le bloc est rendu en réponse libre (axe format) → le
 * corrigé donne le contenu des bonnes réponses, pas leurs lettres.
 */
export const CorrectionNode = ({ block, reponseLibre = false }) => {
    if (block.kind === 'qcm-group') {
        return <QcmGroupCorrection exNum={block.exNum} items={block.items} libre={reponseLibre} />;
    }
    if (block.kind === 'saisie-group') {
        return <SaisieGroupCorrection exNum={block.exNum} items={block.items} />;
    }
    if (block.kind === 'vf-group') {
        return <VfGroupCorrection exNum={block.exNum} items={block.items} libre={reponseLibre} />;
    }
    if (block.kind === 'figure-group') {
        return <FigureGroupCorrection exNum={block.exNum} items={block.items} libre={reponseLibre} />;
    }
    return <SingleCorrection exNum={block.exNum} payload={block.payload} libre={reponseLibre} />;
};

/**
 * Une correction est « demi-largeur » quand elle tient sur une ligne courte
 * (« Réponse : D ») : QCM simple (hors tableau V/F) et saisie simple. Deux
 * demi consécutives dans l'ordre de lecture sont appariées côte à côte par
 * EvalPrintPage. Tout le reste (groupes, V/F, relier, classer, figures…)
 * reste pleine largeur.
 */
const estDemiLargeur = (block) => {
    if (block.kind !== 'single') return false;
    const p = block.payload;
    if (isVraiFaux(p)) return false;
    if (p?.type === 'qcm') {
        // Un QCM à table générique répond DANS le tableau → large
        return !(p.elements_additionnels?.elements || []).some((e) => e?.type === 'table');
    }
    return p?.type === 'texte';
};

/**
 * Fabrique des items de corrigé PAGINABLES — un par bloc, identifié par son
 * index dans `blocks` (stable à travers le renumérotage in-place des exNum,
 * contrairement à `exNum` lui-même). Le tri par exNum (ordre de lecture
 * final) est fait par le CONSOMMATEUR au moment du rendu display, APRÈS
 * renumberBlocks — les hauteurs mesurées se raccrochent par `id`. Le rendu
 * passe par <CorrectionNode block={item.block}/> (numéros lus au rendu).
 *
 * @returns {Array<{ id: number, block: object, half: boolean }>}
 */
export const buildCorrectionItems = (blocks = []) =>
    blocks.map((block, id) => ({
        id,
        block,
        half: estDemiLargeur(block),
    }));
